import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { trackAffiliateClick } from '../utils/metaPixel';
/**
 * Affiliate UTM Helper
 * Automatically appends UTM parameters and SubID tracking to affiliate URLs.
 * Captures UTMs from the landing page URL (set by Meta/Google ads) and forwards them.
 */
function buildAffiliateUrl(baseUrl: string, productSlug: string): string {
  if (typeof window === 'undefined') return baseUrl;
  const urlParams = new URLSearchParams(window.location.search);
  // Capture UTMs from current page (set by ad platform) with sensible defaults
  const utmSource = urlParams.get('utm_source') || 'direct';
  const utmMedium = urlParams.get('utm_medium') || 'organic';
  const utmCampaign = urlParams.get('utm_campaign') || 'blinkblis_hub';
  const utmContent = urlParams.get('utm_content') || productSlug;
  const utmTerm = urlParams.get('utm_term') || '';
  // Click ID for sub-tracking inside Impact (so we can correlate to ad-level data later)
  const clickId = urlParams.get('fbclid') || urlParams.get('gclid') || urlParams.get('ttclid') || `bb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
  // Build SubID payload: source-campaign-product
  const subId = `${utmSource}-${utmCampaign}-${productSlug}`;
  // Append params. Use & if URL already has query params, ? otherwise.
  const separator = baseUrl.includes('?') ? '&' : '?';
  const params = new URLSearchParams({
    subId1: subId,
    subId2: clickId,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_content: utmContent
  });
  if (utmTerm) params.set('utm_term', utmTerm);
  return `${baseUrl}${separator}${params.toString()}`;
}
export function ComparisonTableSection() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const products = [{
    id: 'kovo',
    slug: 'kovo',
    name: 'KOVO',
    number: '01',
    cost: '$10/mo × 24mo',
    bureaus: '4 (TU/EQ/EX/IN)',
    creditCheck: 'No',
    bestFor: 'Beginners building credit',
    bestMatch: true,
    baseUrl: 'https://kovo-credit.sjv.io/MKbb52',
    category: 'Credit Builder',
    description: 'Structured credit-builder path for consumers focused on installment reporting consistency.'
  }, {
    id: 'kikoff',
    slug: 'kikoff',
    name: 'KIKOFF',
    number: '02',
    cost: 'Starting $5/mo',
    bureaus: '3 (TU/EQ/EX)',
    creditCheck: 'No',
    bestFor: 'Fast credit boost',
    bestMatch: false,
    baseUrl: 'https://kikoff.pxf.io/19V149',
    category: 'Credit Builder',
    description: 'Low-cost revolving credit-builder option designed around utilization and reporting history.'
  }, {
    id: 'nav',
    slug: 'nav',
    name: 'NAV',
    number: '03',
    cost: 'Free + Premium',
    bureaus: 'Personal + Business',
    creditCheck: 'Soft pull',
    bestFor: 'Small business owners',
    bestMatch: false,
    baseUrl: 'https://nav.nkwcmr.net/5gvPzL',
    category: 'Business Credit',
    description: 'Business-credit monitoring and financing visibility for founders and self-employed operators.'
  }, {
    id: 'ark7',
    slug: 'ark7',
    name: 'ARK7',
    number: '04',
    cost: 'From $20/share',
    bureaus: 'N/A (Investment)',
    creditCheck: 'No',
    bestFor: 'Passive real estate income',
    bestMatch: false,
    baseUrl: 'https://ark7.sjv.io/9gRmG5',
    category: 'Investment',
    description: 'Alternative asset participation model tied to reporting and investing behavior.'
  }, {
    id: 'bill',
    slug: 'bill',
    name: 'BILL',
    number: '05',
    cost: 'Free trial available',
    bureaus: 'N/A (Accounting)',
    creditCheck: 'No',
    bestFor: 'Business cash flow',
    bestMatch: false,
    baseUrl: 'https://bill.sjv.io/5g6E71',
    category: 'B2B Finance',
    description: 'Business payment infrastructure supporting operational cash-flow organization and vendor management.'
  }];
  // Build live URLs only after mount (so we can read window.location)
  const liveProducts = products.map(p => ({
    ...p,
    url: mounted ? buildAffiliateUrl(p.baseUrl, p.slug) : p.baseUrl
  }));
  return <section id="comparison" className="py-24 lg:py-36 bg-offwhite relative" data-id="element-26">
      {/* Subtle dot grid background */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" data-id="element-27">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, #64748b 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-28"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-29">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-30">
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6" data-id="element-31">
            <span className="text-navy text-xs font-bold tracking-widest uppercase" data-id="element-32">
              Alternative Reporting Frameworks
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-serif text-charcoal mb-4" data-id="element-33">
            Other options if ProCreditWatch is not your fit
          </h2>
          <p className="text-lg text-slate font-medium leading-[1.75] max-w-[65ch] mx-auto" data-id="element-34">
            Different financial situations require different reporting and
            credit-building approaches.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8" data-id="element-35">
          <table className="w-full text-left border-collapse" data-id="element-36">
            <thead data-id="element-37">
              <tr className="bg-slate-50 border-b border-slate-200" data-id="element-38">
                <th className="py-5 px-6 font-bold text-charcoal uppercase tracking-wide text-xs" data-id="element-39">
                  Product
                </th>
                <th className="py-5 px-6 font-bold text-charcoal uppercase tracking-wide text-xs" data-id="element-40">
                  Reporting or Funding Objective
                </th>
                <th className="py-5 px-6 font-bold text-charcoal uppercase tracking-wide text-xs" data-id="element-41">
                  Ongoing Cost Structure
                </th>
                <th className="py-5 px-6 font-bold text-charcoal uppercase tracking-wide text-xs" data-id="element-42">
                  Monitoring Transparency
                </th>
                <th className="py-5 px-6 font-bold text-charcoal uppercase tracking-wide text-xs" data-id="element-43">
                  Best Financial Context
                </th>
                <th className="py-5 px-6 font-bold text-charcoal uppercase tracking-wide text-xs text-right" data-id="element-44">
                  Action
                </th>
              </tr>
            </thead>
            <tbody data-id="element-45">
              {liveProducts.map(product => <tr key={product.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${product.bestMatch ? 'bg-emerald-50/30' : ''}`} data-id="element-46">
                  <td className="py-6 px-6" data-id="element-47">
                    <div className="flex items-start" data-id="element-48">
                      <div className="mr-3 mt-0.5" data-id="element-49">
                        <span className="font-mono font-medium text-gold tracking-wider" data-id="element-50">
                          {product.number} /
                        </span>
                      </div>
                      <div data-id="element-51">
                        <span className="font-bold text-navy-900 block mb-1" data-id="element-52">
                          {product.name}
                        </span>
                        <p className="text-xs text-slate max-w-[200px] leading-relaxed mb-2" data-id="element-53">
                          {product.description}
                        </p>
                        {product.bestMatch && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 cursor-help uppercase tracking-wider" title="Best Match highlights the strongest fit based on reporting visibility, transparency, and workflow control." data-id="element-54">
                            Best Match
                          </span>}
                      </div>
                    </div>
                  </td>
                  <td className="py-6 px-6 align-top" data-id="element-55">
                    <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-700" data-id="element-56">
                      {product.category}
                    </span>
                  </td>
                  <td className="py-6 px-6 text-slate font-medium align-top" data-id="element-57">
                    {product.cost}
                  </td>
                  <td className="py-6 px-6 text-slate font-medium align-top" data-id="element-58">
                    {product.creditCheck}
                  </td>
                  <td className="py-6 px-6 text-slate font-medium align-top" data-id="element-59">
                    {product.bestFor}
                  </td>
                  <td className="py-6 px-6 text-right align-top" data-id="element-60">
                    <a href={product.url} target="_blank" rel="noopener sponsored" data-product={product.slug} onClick={() => trackAffiliateClick(product.slug, product.name)} className="inline-flex items-center gap-2 bg-emerald hover:bg-emerald/90 text-white px-6 py-2 rounded-lg font-bold text-sm transition-all duration-200" data-id="element-61">
                      Get Started
                      <ExternalLink className="w-3.5 h-3.5" data-id="element-62" />
                    </a>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-6 mb-8" data-id="element-63">
          {liveProducts.map(product => <div key={product.id} className={`bg-white rounded-xl shadow-sm p-6 border ${product.bestMatch ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'} hover:shadow-md transition-shadow`} data-id="element-64">
              <div className="flex items-start justify-between mb-4" data-id="element-65">
                <div className="flex items-start" data-id="element-66">
                  <div className="mr-2 mt-0.5" data-id="element-67">
                    <span className="font-mono font-medium text-gold tracking-wider" data-id="element-68">
                      {product.number} /
                    </span>
                  </div>
                  <div data-id="element-69">
                    <h3 className="font-bold text-navy-900 text-lg mb-1" data-id="element-70">
                      {product.name}
                    </h3>
                    {product.bestMatch && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 mb-2 cursor-help uppercase tracking-wider" title="Best Match highlights the strongest fit based on reporting visibility, transparency, and workflow control." data-id="element-71">
                        Best Match
                      </span>}
                  </div>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-700 mt-1" data-id="element-72">
                  {product.category}
                </span>
              </div>

              <p className="text-sm text-slate mb-6 leading-relaxed" data-id="element-73">
                {product.description}
              </p>

              <div className="space-y-3 mb-6" data-id="element-74">
                <div className="flex justify-between border-b border-slate-100 pb-2" data-id="element-75">
                  <span className="text-slate text-sm" data-id="element-76">
                    Ongoing Cost Structure
                  </span>
                  <span className="font-medium text-charcoal text-sm text-right max-w-[50%]" data-id="element-77">
                    {product.cost}
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-2" data-id="element-78">
                  <span className="text-slate text-sm" data-id="element-79">
                    Monitoring Transparency
                  </span>
                  <span className="font-medium text-charcoal text-sm text-right max-w-[50%]" data-id="element-80">
                    {product.creditCheck}
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-100 pb-2" data-id="element-81">
                  <span className="text-slate text-sm" data-id="element-82">
                    Best Financial Context
                  </span>
                  <span className="font-medium text-charcoal text-sm text-right max-w-[50%]" data-id="element-83">
                    {product.bestFor}
                  </span>
                </div>
              </div>

              <a href={product.url} target="_blank" rel="noopener sponsored" data-product={product.slug} onClick={() => trackAffiliateClick(product.slug, product.name)} className="flex items-center justify-center gap-2 w-full bg-emerald hover:bg-emerald/90 text-white px-6 py-3 rounded-lg font-bold transition-all duration-200" data-id="element-84">
                Get Started
                <ExternalLink className="w-4 h-4" data-id="element-85" />
              </a>
            </div>)}
        </div>

        <p className="text-sm text-slate-500 italic text-center max-w-4xl mx-auto" data-id="element-86">
          Advertiser Disclosure: Blinkblis may earn a commission when you sign
          up through our links. This does not affect our editorial
          recommendations or the price you pay. We only recommend products we
          believe genuinely help build credit and grow your financial future.
        </p>
      </div>
    </section>;
}